import { validateValue } from '../validators/base-validator';
import {
  STANDARD_EVENT_NAME,
  EVENT_PROPERTY_NAME_MAX,
  EVENT_PROPERTY_VALUE_MAX,
  EVENT_PROPERTIES_MAX,
} from '../constants';
import { outputErrorMessage } from './error-message';
import { errorTypes } from '../validators/error-types';
import { StandardEventPropertyItem, STANDARD_EVENT_PROPERTY_KEYS, STANDARD_EVENTS } from './standard-event-properties';
import { TriggeredEvent } from '../triggered-event.types';

// Utility ========================================================================================

const hasInvalidKeys = (validKeys: StandardEventPropertyItem[], propKeys: string[]): string[] => {
  const validKeyNames = validKeys.map((key) => key.name);
  return propKeys.filter((key: string) => !validKeyNames.includes(key));
};

const missingRequiredKeys = (requiredKeys: StandardEventPropertyItem[], propKeys: string[]): string[] => {
  const requiredKeyNames = requiredKeys.map((key) => key.name);
  return requiredKeyNames.filter((key: string) => !propKeys.includes(key));
};

const isPredefinedKey = (predefinedKeys: StandardEventPropertyItem[], propKey: string): boolean => {
  const predefinedKeyNames = predefinedKeys.map((key) => key.name);
  return predefinedKeyNames.includes(propKey);
};

const flattenEventProperties = (
  eventType: string,
  requiredProperties: { [key: string]: any },
  optionalProperties: { [key: string]: any }
): { [key: string]: string | number | Date | null } => {
  const flattenedProperties = { ...requiredProperties, ...optionalProperties, ...optionalProperties.extras };

  const keys = Object.keys(optionalProperties);
  for (let i = 0; i < keys.length; i += 1) {
    if (
      !isPredefinedKey(STANDARD_EVENTS[eventType].required, optionalProperties[keys[i]]) &&
      optionalProperties[keys[i]] === undefined
    )
      delete flattenedProperties[keys[i]];
  }

  if (optionalProperties.extras) {
    delete flattenedProperties.extras;

    const extrasKeys = Object.keys(optionalProperties.extras);
    for (let i = 0; i < extrasKeys.length; i += 1) {
      if (optionalProperties.extras[extrasKeys[i]] === undefined) delete flattenedProperties[extrasKeys[i]];
    }
  }

  return flattenedProperties;
};

// Validations ====================================================================================

const validateObjectCapacity = (eventType: string, properties: { [key: string]: any }): boolean => {
  if (Object.keys(properties).length > EVENT_PROPERTIES_MAX) {
    outputErrorMessage(errorTypes.CUSTOM, eventType, {
      custom: `Exceeded maximum number of properties. Please keep the number of properties within ${EVENT_PROPERTIES_MAX}.`,
    });
    return false;
  }
  return true;
};

const validateRequiredPropertyKeys = (eventType: string, properties: { [key: string]: any }): boolean => {
  const missingKeys = missingRequiredKeys(STANDARD_EVENTS[eventType].required, Object.keys(properties));
  if (missingKeys.length > 0) {
    outputErrorMessage(errorTypes.CUSTOM, eventType, {
      custom: `The key(s) "${missingKeys.join('", "')}" are missing.`,
    });
    return false;
  }
  return true;
};

const validateOptionalPropertyKeys = (eventType: string, properties: { [key: string]: any }): boolean => {
  const invalidKeys = hasInvalidKeys(STANDARD_EVENTS[eventType].optional, Object.keys(properties));
  if (invalidKeys.length > 0) {
    outputErrorMessage(errorTypes.CUSTOM, eventType, {
      custom: `The keys "${invalidKeys.join(
        '", "'
      )}" are invalid optional property keys. Please check the appropriate documentation at https://docs.repro.io`,
    });
    return false;
  }
  return true;
};

const validateRequiredPropertyValues = (eventType: string, properties: { [key: string]: any }): boolean => {
  for (let i = 0; i < STANDARD_EVENTS[eventType].required.length; i += 1) {
    const key = STANDARD_EVENTS[eventType].required[i];
    const [result, error] = validateValue(properties[key.name], {
      type: key.type,
      maxStringLength: EVENT_PROPERTY_VALUE_MAX,
      denyEmptyString: true,
    });
    if (!result) {
      outputErrorMessage(error, eventType, {
        subject: `value of property "${key.name}"`,
        maxStringLength: EVENT_PROPERTY_VALUE_MAX,
      });
      return false;
    }
  }
  return true;
};

const validateOptionalPropertyValues = (eventType: string, properties: { [key: string]: any }): boolean => {
  const propertyKeys = Object.keys(properties);

  for (let i = 0; i < STANDARD_EVENTS[eventType].optional.length; i += 1) {
    const key = STANDARD_EVENTS[eventType].optional[i];
    // Skip if EXTRAS or optional property is not set
    if (key !== STANDARD_EVENT_PROPERTY_KEYS.EXTRAS && propertyKeys.includes(key.name)) {
      if (properties[key.name] !== undefined) {
        const [result, error] = validateValue(properties[key.name], {
          type: key.type,
          maxStringLength: EVENT_PROPERTY_VALUE_MAX,
          allowNullString: true,
        });
        if (!result) {
          outputErrorMessage(error, eventType, {
            subject: `value of property "${key.name}"`,
            maxStringLength: EVENT_PROPERTY_VALUE_MAX,
          });
          return false;
        }
      }
    }
  }
  return true;
};

const validateExtrasKey = (eventType: string, key: string, definedKeys: StandardEventPropertyItem[]): boolean => {
  if (isPredefinedKey(definedKeys, key)) {
    outputErrorMessage(errorTypes.CUSTOM, eventType, {
      custom: `${key} is a predefined key. Do not place it within 'extras'.`,
    });
    return false;
  }

  const [result, error] = validateValue(key, {
    type: 'string',
    denyEmptyString: true,
    maxStringLength: EVENT_PROPERTY_NAME_MAX,
  });
  if (!result) {
    outputErrorMessage(error, eventType, {
      subject: `custom property key "${key}"`,
      maxStringLength: EVENT_PROPERTY_NAME_MAX,
    });
    return false;
  }
  return true;
};

const validateExtrasValue = (eventType: string, properties: { [key: string]: any }, key: string): boolean => {
  const [result, error] = validateValue(properties.extras[key], {
    allowNull: true,
    maxStringLength: EVENT_PROPERTY_VALUE_MAX,
  });
  if (!result) {
    outputErrorMessage(error, eventType, {
      subject: `value of property "${key}"`,
      maxStringLength: EVENT_PROPERTY_VALUE_MAX,
    });
    return false;
  }
  return true;
};

// Main ===========================================================================================

export const buildStandardEvent = (
  eventType: string,
  requiredProperties: { [key: string]: any },
  optionalProperties: { [key: string]: any }
): TriggeredEvent | false => {
  if (typeof optionalProperties !== 'object' || optionalProperties instanceof Date) {
    outputErrorMessage(errorTypes.CUSTOM, eventType, {
      custom: `Properties must be declared in an object.`,
    });
    return false;
  }

  const flattenedProperties = flattenEventProperties(eventType, requiredProperties, optionalProperties);

  if (!validateObjectCapacity(eventType, flattenedProperties)) return false;

  if (!validateRequiredPropertyKeys(eventType, requiredProperties)) return false;

  if (!validateOptionalPropertyKeys(eventType, optionalProperties)) return false;

  if (!validateRequiredPropertyValues(eventType, requiredProperties)) return false;

  // Need to separate from required because string values CAN be null
  if (!validateOptionalPropertyValues(eventType, optionalProperties)) return false;

  if (
    optionalProperties.extras &&
    !Object.keys(optionalProperties.extras).every(
      (key) =>
        // Skip if value is undefined - 「指定なし」と同じ
        optionalProperties.extras[key] === undefined ||
        (validateExtrasKey(eventType, key, [
          ...STANDARD_EVENTS[eventType].required,
          ...STANDARD_EVENTS[eventType].optional,
        ]) &&
          validateExtrasValue(eventType, optionalProperties, key))
    )
  ) {
    return false;
  }

  return {
    name: STANDARD_EVENT_NAME(STANDARD_EVENTS[eventType].name),
    properties: flattenedProperties,
    tracked_at: new Date(),
  } as TriggeredEvent;
};
